import { ChevronDown } from 'lucide-react';
import React from 'react';
import {
  ComboBox as AriaComboBox,
  ComboBoxProps as AriaComboBoxProps,
  ListBox,
  ListBoxItemProps,
  ValidationResult,
} from 'react-aria-components';
import { Button } from '@/components/v2/AriaButton';
import { Description, FieldError, FieldGroup, Input, Label } from './Field';
import { DropdownItem, DropdownSection, DropdownSectionProps } from '../ListBox';
import { Popover } from '../../Popover';
import { composeTailwindRenderProps } from '@/utils/utils';

export interface ComboBoxProps<T extends object> extends Omit<AriaComboBoxProps<T>, 'children'> {
  label?: string;
  description?: string | null;
  errorMessage?: string | ((validation: ValidationResult) => string);
  children: React.ReactNode | ((item: T) => React.ReactNode);
  placeholder?: string;
  testId?: string;
}

export function ComboBox<T extends object>({
  label,
  description,
  errorMessage,
  children,
  items,
  testId,
  ...props
}: ComboBoxProps<T>) {
  return (
    <AriaComboBox
      menuTrigger="focus"
      {...props}
      className={composeTailwindRenderProps(props.className, 'group flex flex-col gap-1 text-xs')}>
      {label && <Label aria-required>{label}</Label>}
      <FieldGroup>
        <Input className="overflow-hidden" />
        <Button
          data-cy={testId}
          variant="icon"
          className="w-6 mr-1 rounded outline-offset-0 bg-zinc-100 z-10">
          <ChevronDown aria-hidden className="w-4 h-4 bg-zinc-100 text-zinc-800" />
        </Button>
      </FieldGroup>
      {description && <Description>{description}</Description>}
      <FieldError>{errorMessage}</FieldError>
      <Popover className="w-[--trigger-width] text-xs">
        <ListBox
          items={items}
          className="outline-0 text-xs text-zinc-800 p-1 max-h-[inherit] overflow-auto [clip-path:inset(0_0_0_0_round_.75rem)]">
          {children}
        </ListBox>
      </Popover>
    </AriaComboBox>
  );
}

export function ComboBoxItem(props: ListBoxItemProps) {
  return <DropdownItem className="text-xs" {...props} />;
}

export function ComboBoxSection<T extends object>(props: DropdownSectionProps<T>) {
  return <DropdownSection className="text-xs" {...props} />;
}
